import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AuthService } from './auth.service';
import {
  catchError,
  first,
  from,
  map,
  Observable,
  of,
  switchMap
} from 'rxjs';
import { InactivityService } from './inactivity.service';
import { StopInactivityServiceGuard } from './stop-inactivity-service-guard.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {
  private readonly visibilityChangeHandler = () => {
    // MFA has no authenticated session yet. A guard created during an earlier
    // signed-in visit must not interrupt it (or another public route) on return.
    if (document.visibilityState !== 'visible' || this.authService.isPublicRoute(this.router.url)) {
      return;
    }

    this.inactivityService.syncSessionState();
    this.authService.checkSession().pipe(first()).subscribe(isAuthenticated => {
      // Navigation can finish while an asynchronous session check is pending.
      if (!isAuthenticated && !this.authService.isPublicRoute(this.router.url)) {
        this.unauthenticatedLogic();
      }
    });
  };

  constructor(
    private authService: AuthService, private router: Router,
    private inactivityService: InactivityService,
    private stopInactivity: StopInactivityServiceGuard,
  ) {
    this.setupVisibilityChangeHandling();
  }

  canActivate(): Observable<boolean> {
    console.log('AuthGuard#canActivate called');
    return this.authService.checkSession().pipe(
      switchMap(isAuthenticated => {
        console.log('Is Authenticated:', isAuthenticated);
        if (!isAuthenticated) {
          this.unauthenticatedLogic();
          return of(false);
        }

        return from(this.authService.ensurePolicyIdsLoaded()).pipe(
          map(() => true),
          catchError(error => {
            // Keep the session alive if policy bootstrap fails unexpectedly.
            console.error('Error ensuring policy ids:', error);
            return of(true);
          })
        );
      }),
      catchError(error => {
        console.error('Error during session check:', error);
        this.unauthenticatedLogic();
        return of(false);
      }),
      first()  // Ensure the Observable completes by taking only the first emitted value
    );
  }

  private setupVisibilityChangeHandling(): void {
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private unauthenticatedLogic(){
    console.log('Session expired, redirecting to login...');
    this.authService.logout();
    this.inactivityService.stopWatching();
    this.stopInactivity.canActivateLogOut();
    location.href = '/login';
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
  }
}
